import { type ReactNode, useEffect } from 'react';

import { MAIL_CONTENT_KINDS } from 'cpool-constants';
import type { SchemaTemplateResolver_GetSignature, SchemaUserResolver_Get } from 'cpool-tables/backend-resolvers';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Footer } from './Footer';
import { Header } from './Header';
import { useOwnMailHeaderFooter } from './hooks/useOwnMailHeaderFooter';
import { HtmlMailPreview } from './HtmlMailPreview';
import type { EmailComposerForm } from './schemas/emailComposerForm.schema';
import { Signature } from './Signature';
import type { InputKind } from '../headerFooterTemplate/types';
import { cn } from '~/utils/cnTailwind';

type Props = {
    content: ReactNode;
    contentKind: InputKind;
    formRevision: number;
    getValues: UseFormGetValues<EmailComposerForm>;
    setValue: UseFormSetValue<EmailComposerForm>;
    signature: SchemaTemplateResolver_GetSignature | null;
    subject?: string | null;
    templateId?: string;
    user: SchemaUserResolver_Get | undefined;
};

export const MailPreview: React.FC<Props> = ({
    content,
    contentKind,
    formRevision,
    getValues,
    setValue,
    signature,
    subject,
    templateId,
    user,
}) => {
    const { t } = useTranslation();
    const isHtmlContent = contentKind === MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT;
    const { isError } = useOwnMailHeaderFooter();

    useEffect(() => {
        if (isError)
            toast.error(t('HEADER_FOOTER_TEMPLATE.TOASTS.LOAD_ERROR'), {
                toastId: 'own-mail-header-footer-load-error',
            });
    }, [isError, t]);

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
            <span className="sr-only">{t('MESSAGE.EMAIL.SUBJECT')}</span>
            <div className="flex h-8 w-full shrink-0 items-center rounded border border-grey-outline-light bg-white px-3">
                <p className="truncate text-sm text-midnight">{subject}</p>
            </div>
            <span className="sr-only">{t('MESSAGE.EMAIL.TEXT')}</span>
            <div
                className={cn(
                    'min-h-0 w-full flex-1 overflow-y-auto rounded-md border border-grey-outline-light bg-white py-[18px] pr-[18px] text-sm text-midnight',
                    // The HTML preview mimics the rendered mail, which is a narrow centred column.
                    // The text preview has no such layout, so it uses the full width instead.
                    isHtmlContent ? 'pl-[clamp(18px,18.8%,184px)]' : 'pl-[18px]',
                )}
                data-testid="mail-content"
            >
                <div className={isHtmlContent ? 'w-[581px] max-w-full' : 'w-full'} data-testid="mail-content-column">
                    <Header contentKind={contentKind} user={user} />
                    {!isHtmlContent ? (
                        content
                    ) : (
                        // The key resets the raw HTML inputs only when another template is selected.
                        <HtmlMailPreview
                            key={templateId}
                            content={content as string}
                            getValues={getValues}
                            revision={formRevision}
                            setValue={setValue}
                        />
                    )}
                    <Signature contentKind={contentKind} signature={signature} user={user} />
                    <Footer contentKind={contentKind} user={user} />
                </div>
            </div>
        </div>
    );
};
